import type {
  AgAreaSeriesOptions,
  AgBarSeriesOptions,
  AgCartesianSeriesOptions
} from 'ag-charts-enterprise';
import { createSearchParams } from 'react-router';

import { PERMISSIONS } from '@/constants/auth.constants';
import { VMP_VULNERABILITY_COLORS } from '@/constants/chartcolors.constants';
import { PERMISSIONS_LOGIC } from '@/constants/common';
import PREFERENCES from '@/constants/preferences.constants';
import { LOG_PRODUCTS, PRODUCTS } from '@/constants/products.constants';

import { TICKET_STATUS } from '../Tickets/Tickets.constants';
import type { MyCyberScorecardDTO } from './Dashboard.types';

export const DASHBOARD_CONSTANTS = [
  {
    label: 'Summary',
    path: '/dashboard',
    value: 0,
    ariaLabel: 'summary-tab',
    requiredPermissions: 'exempt'
  },
  {
    label: 'Tickets',
    path: '/dashboard/tickets',
    value: 1,
    ariaLabel: 'tickets-tab',
    requiredPermissions: PERMISSIONS.TICKETS_READ
  },
  {
    label: 'Cases',
    path: '/dashboard/cases',
    value: 2,
    ariaLabel: 'cases-tab',
    requiredPermissions: PERMISSIONS.CASES_READ
  },
  {
    label: 'Usage',
    path: '/dashboard/usage',
    value: 3,
    ariaLabel: 'usage-tab',
    requiredPermissions: 'exempt',
    requiredProducts: {
      products: LOG_PRODUCTS,
      logic: PERMISSIONS_LOGIC.OR
    }
  },
  {
    label: 'Vulnerability Management',
    path: '/dashboard/vmp',
    value: 4,
    ariaLabel: 'vmp-tab',
    requiredPermissions: 'exempt',
    requiredProducts: { products: [PRODUCTS.VMPE] }
  },
  {
    label: 'Internal',
    path: '/dashboard/internal',
    value: 5,
    ariaLabel: 'internal-tab',
    requiredPermissions: PERMISSIONS.INTERNAL
  },
  {
    label: 'MyCyberScorecard',
    path: '/dashboard/mcsc',
    value: 6,
    ariaLabel: 'mcsc-tab',
    requiredPermissions: 'exempt',
    requiredPreferences: [PREFERENCES.MCSC]
  }
  // {
  //   label: 'Events',
  //   path: '/dashboard/events',
  //   value: 4,
  //   ariaLabel: 'events-tab',
  //   permissions: 'exempt'
  // },
  // {
  //   label: 'Assets',
  //   path: '/dashboard/assets',
  //   value: 5,
  //   ariaLabel: 'assets-tab'
  // }
];

export const DASHBOARD_TIMEFRAMES = [
  { label: '7 days', value: 7 },
  { label: '1 month', value: 30 },
  { label: '3 months', value: 90 },
  { label: '6 months', value: 180 }
];

export const DEFAULT_MCSC_ASSESSMENT: MyCyberScorecardDTO = {
  assessment_id: 256,
  assessment_name: 'NIST/HIPAA 2023.4',
  assessment_type: 'NIST Standard',
  assessment_type_id: 3,
  completed_at: '2023-12-28T00:00:00',
  controls_completed: 108,
  controls_total: 108,
  created_at: '2025-04-27T02:25:47',
  id: 34,
  my_cyber_risk: {
    total: 2.4,
    target: 10,
    progress: 100,
    functions: {
      DETECT: 2.3,
      GOVERN: 2.2,
      IDENTIFY: 2.4,
      PROTECT: 2.5,
      RECOVER: 3.2,
      RESPOND: 2.2
    },
    risk_items: {
      Critical: 1,
      High: 16,
      Medium: 24,
      Low: 67
    }
  },
  my_cyber_score: {
    total: 844,
    target: 1071,
    progress: 100,
    functions: {
      DETECT: { score: 141, target: 180, percent_completion: 78.33 },
      GOVERN: { score: 200, target: 250, percent_completion: 80 },
      IDENTIFY: { score: 503, target: 641, percent_completion: 78.41 },
      PROTECT: { score: 243, target: 321, percent_completion: 75.78 },
      RECOVER: { score: 112, target: 112, percent_completion: 100 },
      RESPOND: { score: 104, target: 120, percent_completion: 86.67 }
    },
    categories: {
      PEOPLE: { score: 141, target: 180 },
      PROCESS: { score: 200, target: 250 },
      TECHNOLOGY: { score: 503, target: 641 }
    }
  },
  risk_impact: 4,
  status: 'Inactive',
  status_id: 1,
  updated_at: '2025-04-27T02:25:52'
};

export const STACKED_AREA_SERIES: AgAreaSeriesOptions[] = [
  {
    type: 'area',
    xKey: 'date',
    yKey: 'CRITICAL',
    yName: 'Critical',
    stacked: true,
    fill: VMP_VULNERABILITY_COLORS.CRITICAL,
    strokeWidth: 3,
    stroke: VMP_VULNERABILITY_COLORS.CRITICAL,
    fillOpacity: 0.4,
    marker: {
      enabled: true,
      fill: VMP_VULNERABILITY_COLORS.CRITICAL
    }
  },
  {
    type: 'area',
    xKey: 'date',
    yKey: 'HIGH',
    yName: 'High',
    stacked: true,
    fill: VMP_VULNERABILITY_COLORS.HIGH,
    strokeWidth: 3,
    fillOpacity: 0.4,
    stroke: VMP_VULNERABILITY_COLORS.HIGH,
    marker: {
      enabled: true,
      fill: VMP_VULNERABILITY_COLORS.HIGH
    }
  },
  {
    type: 'area',
    xKey: 'date',
    yKey: 'MEDIUM',
    yName: 'Medium',
    stacked: true,
    fill: VMP_VULNERABILITY_COLORS.MEDIUM,
    strokeWidth: 3,
    stroke: VMP_VULNERABILITY_COLORS.MEDIUM,
    fillOpacity: 0.4,
    marker: {
      enabled: true,
      fill: VMP_VULNERABILITY_COLORS.MEDIUM
    }
  },
  {
    type: 'area',
    xKey: 'date',
    yKey: 'LOW',
    yName: 'Low',
    stacked: true,
    stroke: VMP_VULNERABILITY_COLORS.LOW,
    fill: VMP_VULNERABILITY_COLORS.LOW,
    fillOpacity: 0.4,
    strokeWidth: 3,
    marker: {
      enabled: true,
      fill: VMP_VULNERABILITY_COLORS.LOW
    },
    visible: false
  }
];

export const VMPE_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    type: 'bar',
    xKey: 'name',
    xName: 'Category',
    yKey: 'count',
    yName: 'Counts',
    direction: 'horizontal',
    fill: '#B284BE'
  }
];

export const CASES_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    type: 'bar',
    xKey: 'name',
    xName: 'Category',
    yKey: 'count',
    yName: 'Counts',
    direction: 'horizontal',
    fill: '#008ffb'
  }
];

export const CASES_EVENTS_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    type: 'bar',
    xKey: 'date',
    yKey: 'cases',
    yName: 'Cases',
    fill: '#aa6ec1'
  },
  {
    type: 'bar',
    xKey: 'date',
    yKey: 'events',
    yKeyAxis: 'ySecondary',
    yName: 'Events',
    fill: '#47838b'
  }
];

export const MCSC_FUNCTION_COMPLETION_BAR_SERIES: AgBarSeriesOptions[] = [
  {
    type: 'bar',
    xKey: 'name',
    yKey: 'total',
    yName: 'Total'
  },
  {
    type: 'bar',
    xKey: 'name',
    yKey: 'target',
    yName: 'Target'
  }
];

export const CASES_BAR_LINE_SERIES: AgCartesianSeriesOptions[] = [
  {
    type: 'bar',
    xKey: 'date',
    yKey: 'cases',
    yName: 'Cases',
    fill: '#008ffb'
  },
  {
    type: 'line',
    xKey: 'date',
    yKey: 'averageCases',
    yName: 'Average',
    stroke: '#008ffb',
    lineDash: [8, 8],
    marker: {
      enabled: false
    }
  }
];

export const DASHBOARD_TICKET_GROUP_BY_FIELDS: string[] = [
  'ticket_priority__name',
  'ticket_status__name'
];

export const DASHBOARD_ALL_TICKET_GROUP_BY_FIELDS: string[] = [
  'ticket_priority__name',
  'ticket_status__name',
  'ticket_category__name',
  'resolution_id'
];

export const DASHBOARD_THREAT_TICKET_GROUP_BY_FIELDS: string[] = [
  'ticket_priority__name',
  'ticket_status__name',
  'ticket_category__name',
  'resolution_id',
  'ticket_type'
];

export const DASHBOARD_SERVICE_TICKET_GROUP_BY_FIELDS: string[] = [
  'ticket_priority__name',
  'ticket_status__name',
  'ticket_category__name',
  'ticket_type'
];

export const DASHBOARD_VULNERABILITIES_GROUP_BY_FIELDS: string[] = [
  'cvss_level'
];

export const TICKET_QUERY_PARAMS = createSearchParams({
  page: '1',
  page_size: '10'
});

export const OPEN_TICKET_STATUS: string[] = [
  TICKET_STATUS.OPEN,
  TICKET_STATUS.ACKNOWLEDGED
];

export default DASHBOARD_CONSTANTS;
